import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Anchor, Badge, Button, Card, Group, Modal, Skeleton, Stack, Text, TextInput, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { ReferrerType } from "@safiri/shared";
import { DataTable, type Column } from "../../components/common/DataTable";
import { StatusBadge } from "../../components/common/StatusBadge";
import { QueryError } from "../../components/common/QueryError";
import { referralsApi, type InvitedRow } from "../../api/referrals";
import { usePermission } from "../../auth/usePermission";
import { errorMessage } from "../../lib/axios";
import { formatMoney } from "../../lib/format";

/**
 * Driver profile → Referrals (spec §9): the drivers they invited, who
 * invited them, and attaching or changing their referrer with a reason
 * (only until the referrer's first reward is paid).
 */
export function DriverReferralsTab({ driverId }: { driverId: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManage = usePermission("referrals.manage");
  const q = useQuery({ queryKey: ["driver-referrals", driverId], queryFn: () => referralsApi.driver(driverId) });
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");

  const attach = useMutation({
    mutationFn: () => referralsApi.attach(driverId, code.trim(), reason.trim()),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Referrer saved and logged" });
      setOpen(false);
      setCode("");
      setReason("");
      qc.invalidateQueries({ queryKey: ["driver-referrals", driverId] });
    },
    onError: (e) => notifications.show({ color: "red", message: errorMessage(e) }),
  });

  if (q.isLoading) return <Skeleton h={200} />;
  if (q.error) return <QueryError error={q.error} onRetry={q.refetch} />;
  const d = q.data!;
  const by = d.invitedBy;
  const canChange = canManage && (!by || !by.tierOnePaid);

  const columns: Column<InvitedRow>[] = [
    { key: "n", header: "Driver", render: (r) => <Stack gap={0}><Text size="sm">{r.name}</Text><Text size="xs" c="dimmed">{r.phoneNumber}</Text></Stack> },
    { key: "ds", header: "Driver status", render: (r) => <StatusBadge status={r.driverStatus} /> },
    { key: "t", header: "Trips", render: (r) => r.tripCount, align: "right" },
    { key: "s", header: "Reward", render: (r) => <StatusBadge status={r.status} /> },
    { key: "p", header: "Paid", render: (r) => `${formatMoney(r.paid)} / ${formatMoney(r.max)}`, align: "right" },
  ];

  return (
    <Stack>
      <Card padding="lg">
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Invited by</Text>
            {by ? (
              <Group gap={6}>
                <Anchor fw={600} onClick={() => navigate(by.type === ReferrerType.AGENT ? `/referrals/agents/${by.id}` : `/drivers/${by.id}`)}>
                  {by.name || by.code}
                </Anchor>
                <Badge variant="light">{by.type}</Badge>
                <Text size="sm" c="dimmed">{by.code}</Text>
                <Anchor size="sm" onClick={() => navigate(`/referrals/${by.referralId}`)}>View referral</Anchor>
                <StatusBadge status={by.status} />
              </Group>
            ) : (
              <Text size="sm" c="dimmed">Nobody: this driver signed up without a referral code.</Text>
            )}
            {by?.tierOnePaid && <Text size="xs" c="dimmed">The referrer can't be changed: a reward has already been paid.</Text>}
          </Stack>
          <Stack gap={4} align="flex-end">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Their own code</Text>
            <Text fw={650}>{d.code ?? "Issued once approved"}</Text>
            {canChange && (
              <Button size="xs" variant="light" onClick={() => setOpen(true)}>
                {by ? "Change referrer" : "Attach referrer"}
              </Button>
            )}
          </Stack>
        </Group>
      </Card>

      <Text fw={650}>Drivers they invited</Text>
      <DataTable columns={columns} rows={d.invited} rowKey={(r) => r.id} emptyLabel="Hasn't invited anyone yet" onRowClick={(r) => navigate(`/referrals/${r.id}`)} />

      <Modal opened={open} onClose={() => setOpen(false)} title={by ? "Change referrer" : "Attach referrer"}>
        <Stack>
          <Text size="sm" c="dimmed">
            Enter the agent's or driver's code. The same fraud checks as at sign-up apply. Recorded in the audit log.
          </Text>
          <TextInput label="Referral code" placeholder="AG… or DR…" value={code} onChange={(e) => setCode(e.currentTarget.value.toUpperCase())} />
          <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.currentTarget.value)} minRows={2} />
          <Button onClick={() => attach.mutate()} loading={attach.isPending} disabled={code.trim().length < 8 || reason.trim().length < 3}>
            Save
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
